import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, IconButton, Button, CircularProgress,
  ToggleButtonGroup, ToggleButton, Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloseOutlined, CropOutlined, RestartAltOutlined,
  AspectRatioOutlined, CheckOutlined,
} from '@mui/icons-material';
import { api } from '../../../hooks/useApi';

const PRIMARY = '#1075BA';
const HANDLE_SIZE = 10;

const ASPECT_PRESETS = [
  { label: 'Free',  value: null },
  { label: '1∶1',   value: 1 },
  { label: '4∶3',   value: 4 / 3 },
  { label: '16∶9',  value: 16 / 9 },
  { label: '3∶2',   value: 3 / 2 },
  { label: '9∶16',  value: 9 / 16 },
];

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/**
 * CropEditorModal
 *
 * Props:
 *   open              — boolean
 *   onClose           — () => void
 *   item              — MediaAsset object
 *   onSaved           — (updatedItem) => void
 *   forcedAspectRatio — number | null  (e.g. 3 for 1200/400 banner)
 *                       When set: hides preset toolbar, locks ratio
 */
export function CropEditorModal({ open, onClose, item, onSaved, forcedAspectRatio = null }) {
  const canvasRef    = useRef(null);
  const imgRef       = useRef(null);
  const rafRef       = useRef(null);
  const containerRef = useRef(null);

  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
  const [imgLoaded,  setImgLoaded]  = useState(false);
  const [imgError,   setImgError]   = useState(false);
  const [crop,       setCrop]       = useState({ x: 0, y: 0, w: 0, h: 0 });

  // Active aspect ratio: forced takes priority, otherwise user-chosen preset
  const [aspectPreset, setAspectPreset] = useState(null);
  const activeAR = forcedAspectRatio ?? aspectPreset;

  const dragRef = useRef(null);

  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState('');

  // ── Compute canvas size to fit inside container ───────────────────────────

  const computeCanvasSize = useCallback(() => {
    const img  = imgRef.current;
    if (!img || !img.naturalWidth) return;
    const maxW = containerRef.current?.clientWidth  || 700;
    const maxH = containerRef.current?.clientHeight || 460;
    const scale = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
    setCanvasSize({ w: Math.round(img.naturalWidth * scale), h: Math.round(img.naturalHeight * scale) });
  }, []);

  // ── Load image on open ────────────────────────────────────────────────────

  useEffect(() => {
    if (!open || !item) return;
    setImgLoaded(false);
    setImgError(false);
    setSaveError('');
    setAspectPreset(null);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { imgRef.current = img; setImgLoaded(true); };
    img.onerror = () => setImgError(true);
    img.src = item.originalUrl || item.url;
  }, [open, item]);

  useEffect(() => {
    if (!imgLoaded) return;
    computeCanvasSize();
  }, [imgLoaded, computeCanvasSize]);

  // ── Init crop when canvas resizes ─────────────────────────────────────────

  useEffect(() => {
    if (!canvasSize.w) return;
    const ar = activeAR;
    const { w: cw, h: ch } = canvasSize;

    if (ar) {
      let nw = cw * 0.9;
      let nh = nw / ar;
      if (nh > ch * 0.9) { nh = ch * 0.9; nw = nh * ar; }
      setCrop({ x: (cw - nw) / 2, y: (ch - nh) / 2, w: nw, h: nh });
    } else {
      const pad = 0.1;
      setCrop({ x: cw * pad, y: ch * pad, w: cw * (1 - pad * 2), h: ch * (1 - pad * 2) });
    }
  }, [canvasSize]); // eslint-disable-line

  // ── Draw ──────────────────────────────────────────────────────────────────

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img    = imgRef.current;
    if (!canvas || !img || !canvasSize.w) return;
    const ctx = canvas.getContext('2d');
    const { w: cw, h: ch } = canvasSize;

    canvas.width  = cw;
    canvas.height = ch;
    ctx.clearRect(0, 0, cw, ch);

    ctx.drawImage(img, 0, 0, cw, ch);

    // Dim outside crop
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    const { x, y, w, h } = crop;
    ctx.fillRect(0, 0, cw, y);
    ctx.fillRect(0, y + h, cw, ch - y - h);
    ctx.fillRect(0, y, x, h);
    ctx.fillRect(x + w, y, cw - x - w, h);

    // Border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth   = 1.5;
    ctx.strokeRect(x, y, w, h);

    // Rule-of-thirds grid
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth   = 0.75;
    for (let i = 1; i < 3; i++) {
      ctx.beginPath(); ctx.moveTo(x + (w / 3) * i, y); ctx.lineTo(x + (w / 3) * i, y + h); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x, y + (h / 3) * i); ctx.lineTo(x + w, y + (h / 3) * i); ctx.stroke();
    }

    // Handles — edge handles are hidden when ratio is locked
    const drawHandle = (hx, hy, dimmed = false) => {
      ctx.shadowColor = dimmed ? 'transparent' : 'rgba(0,0,0,0.35)';
      ctx.shadowBlur  = dimmed ? 0 : 4;
      ctx.fillStyle   = dimmed ? 'rgba(255,255,255,0.25)' : '#fff';
      ctx.beginPath();
      ctx.roundRect(hx - HANDLE_SIZE / 2, hy - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE, 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    };
    // Corner handles
    drawHandle(x,         y);
    drawHandle(x + w,     y);
    drawHandle(x,         y + h);
    drawHandle(x + w,     y + h);
    // Edge handles — dimmed (and non-interactive) when ratio is locked
    const edgeDimmed = !!activeAR;
    drawHandle(x + w / 2, y,         edgeDimmed);
    drawHandle(x + w / 2, y + h,     edgeDimmed);
    drawHandle(x,         y + h / 2, edgeDimmed);
    drawHandle(x + w,     y + h / 2, edgeDimmed);
  }, [canvasSize, crop, activeAR]);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(draw);
  }, [draw]);

  // ── Hit-test ──────────────────────────────────────────────────────────────
  // When ratio is locked, edge handles (N/S/E/W) are disabled so the user
  // can only drag corner handles, guaranteeing the ratio is never broken.

  const hitTest = useCallback((mx, my) => {
    const { x, y, w, h } = crop;
    const hs = HANDLE_SIZE + 4;
    const near = (px, py) => Math.abs(mx - px) < hs && Math.abs(my - py) < hs;
    // Corner handles — always active
    if (near(x,         y))     return 'nw';
    if (near(x + w,     y))     return 'ne';
    if (near(x,         y + h)) return 'sw';
    if (near(x + w,     y + h)) return 'se';
    // Edge handles — only available when ratio is free
    if (!activeAR) {
      if (near(x + w / 2, y))         return 'n';
      if (near(x + w / 2, y + h))     return 's';
      if (near(x,         y + h / 2)) return 'w';
      if (near(x + w,     y + h / 2)) return 'e';
    }
    if (mx > x && mx < x + w && my > y && my < y + h) return 'move';
    return null;
  }, [crop, activeAR]);

  const cursorMap = {
    nw: 'nw-resize', ne: 'ne-resize', sw: 'sw-resize', se: 'se-resize',
    n:  'n-resize',  s:  's-resize',  w:  'w-resize',  e:  'e-resize',
    move: 'move',
  };

  // ── Mouse ─────────────────────────────────────────────────────────────────

  const handleMouseMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    if (!dragRef.current) {
      const hit = hitTest(mx, my);
      canvas.style.cursor = hit ? (cursorMap[hit] || 'default') : 'default';
      return;
    }

    const { type, startX, startY, startCrop: sc } = dragRef.current;
    const dx = mx - startX;
    const dy = my - startY;
    const { w: cw, h: ch } = canvasSize;
    const ar = activeAR;

    let { x, y, w, h } = sc;

    const applyAR = (newW, newH) => {
      if (!ar) return { w: newW, h: newH };
      return Math.abs(dx) >= Math.abs(dy)
        ? { w: newW, h: newW / ar }
        : { w: newH * ar, h: newH };
    };

    if (type === 'move') {
      x = clamp(sc.x + dx, 0, cw - sc.w);
      y = clamp(sc.y + dy, 0, ch - sc.h);
    } else if (type === 'nw') {
      const d = applyAR(sc.x + sc.w - clamp(sc.x + dx, 0, sc.x + sc.w - 10), sc.y + sc.h - clamp(sc.y + dy, 0, sc.y + sc.h - 10));
      w = d.w; h = d.h; x = sc.x + sc.w - w; y = sc.y + sc.h - h;
    } else if (type === 'ne') {
      const d = applyAR(clamp(sc.w + dx, 10, cw - sc.x), sc.y + sc.h - clamp(sc.y + dy, 0, sc.y + sc.h - 10));
      w = d.w; h = d.h; y = sc.y + sc.h - h;
    } else if (type === 'sw') {
      const d = applyAR(sc.x + sc.w - clamp(sc.x + dx, 0, sc.x + sc.w - 10), clamp(sc.h + dy, 10, ch - sc.y));
      w = d.w; h = d.h; x = sc.x + sc.w - w;
    } else if (type === 'se') {
      const d = applyAR(clamp(sc.w + dx, 10, cw - sc.x), clamp(sc.h + dy, 10, ch - sc.y));
      w = d.w; h = d.h;
    } else if (type === 'n') {
      const nh = clamp(sc.y + dy, 0, sc.y + sc.h - 10);
      h = sc.y + sc.h - nh; y = nh;
      if (ar) { w = h * ar; x = sc.x + (sc.w - w) / 2; }
    } else if (type === 's') {
      h = clamp(sc.h + dy, 10, ch - sc.y);
      if (ar) { w = h * ar; x = sc.x + (sc.w - w) / 2; }
    } else if (type === 'w') {
      const nx = clamp(sc.x + dx, 0, sc.x + sc.w - 10);
      w = sc.x + sc.w - nx; x = nx;
      if (ar) { h = w / ar; y = sc.y + (sc.h - h) / 2; }
    } else if (type === 'e') {
      w = clamp(sc.w + dx, 10, cw - sc.x);
      if (ar) { h = w / ar; y = sc.y + (sc.h - h) / 2; }
    }

    x = clamp(x, 0, cw - 1);
    y = clamp(y, 0, ch - 1);
    w = clamp(w, 10, cw - x);
    h = clamp(h, 10, ch - y);

    setCrop({ x, y, w, h });
  }, [hitTest, canvasSize, activeAR]); // eslint-disable-line

  const handleMouseDown = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const type = hitTest(e.clientX - rect.left, e.clientY - rect.top);
    if (!type) return;
    dragRef.current = { type, startX: e.clientX - rect.left, startY: e.clientY - rect.top, startCrop: { ...crop } };
  }, [hitTest, crop]);

  const handleMouseUp = useCallback(() => { dragRef.current = null; }, []);

  // ── Aspect preset (only when not forced) ─────────────────────────────────

  const handleAspectChange = (_, val) => {
    if (forcedAspectRatio) return;
    setAspectPreset(val);
    if (!val) return;
    const cx = crop.x + crop.w / 2;
    const cy = crop.y + crop.h / 2;
    let nw = crop.w;
    let nh = nw / val;
    if (nh > canvasSize.h) { nh = canvasSize.h * 0.9; nw = nh * val; }
    setCrop({
      x: clamp(cx - nw / 2, 0, canvasSize.w - nw),
      y: clamp(cy - nh / 2, 0, canvasSize.h - nh),
      w: nw, h: nh,
    });
  };

  // ── Reset ─────────────────────────────────────────────────────────────────

  const handleReset = () => {
    const ar = activeAR;
    const { w: cw, h: ch } = canvasSize;
    if (ar) {
      let nw = cw * 0.9;
      let nh = nw / ar;
      if (nh > ch * 0.9) { nh = ch * 0.9; nw = nh * ar; }
      setCrop({ x: (cw - nw) / 2, y: (ch - nh) / 2, w: nw, h: nh });
    } else {
      const pad = 0.1;
      setCrop({ x: cw * pad, y: ch * pad, w: cw * (1 - pad * 2), h: ch * (1 - pad * 2) });
    }
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!item || !canvasSize.w) return;
    setSaving(true); setSaveError('');
    const img    = imgRef.current;
    const scaleX = img.naturalWidth  / canvasSize.w;
    const scaleY = img.naturalHeight / canvasSize.h;
    try {
      const { data } = await api.post(`/media/${item._id}/crop`, {
        x:      Math.round(crop.x * scaleX),
        y:      Math.round(crop.y * scaleY),
        width:  Math.round(crop.w * scaleX),
        height: Math.round(crop.h * scaleY),
      });
      onSaved(data.data);
      onClose();
    } catch (err) {
      setSaveError(err?.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Pixel readout ─────────────────────────────────────────────────────────

  const pixelInfo = (() => {
    if (!imgRef.current || !canvasSize.w) return null;
    return {
      w: Math.round(crop.w * (imgRef.current.naturalWidth  / canvasSize.w)),
      h: Math.round(crop.h * (imgRef.current.naturalHeight / canvasSize.h)),
    };
  })();

  // ── Forced ratio label ────────────────────────────────────────────────────

  const forcedLabel = (() => {
    if (!forcedAspectRatio) return null;
    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const scale = 10;
    const w = Math.round(forcedAspectRatio * scale);
    const h = scale;
    const d = gcd(w, h);
    return `${w / d}:${h / d} locked`;
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          height: '92vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        py: 1.5, px: 2.5,
        borderBottom: '1px solid #e8edf2',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{
            width: 30, height: 30, borderRadius: '8px',
            background: alpha(PRIMARY, 0.1),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CropOutlined sx={{ fontSize: 16, color: PRIMARY }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a2e', lineHeight: 1 }}>
              Crop Image
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: '#9ea8b3', mt: 0.25 }}>
              {item?.displayName || item?.originalName || ''}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {pixelInfo && (
            <Typography sx={{
              fontSize: '0.65rem', color: '#5a6474',
              fontFamily: 'monospace',
              background: '#f4f7fa',
              border: '1px solid #e8edf2',
              px: 1, py: 0.4, borderRadius: '6px',
            }}>
              {pixelInfo.w} × {pixelInfo.h} px
            </Typography>
          )}
          <IconButton size="small" onClick={onClose} sx={{ color: '#b0bac8', '&:hover': { color: '#374151' } }}>
            <CloseOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ── Toolbar ── */}
      <Box sx={{
        px: 2.5, py: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid #f0f4f8',
        background: '#fafbfc',
        flexShrink: 0, gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AspectRatioOutlined sx={{ fontSize: 15, color: '#9ea8b3' }} />
          <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3', mr: 0.5 }}>Ratio</Typography>

          {forcedAspectRatio ? (
            <Box sx={{
              px: 1.25, py: 0.4, borderRadius: '7px',
              background: alpha(PRIMARY, 0.07),
              border: `1px solid ${alpha(PRIMARY, 0.2)}`,
              display: 'flex', alignItems: 'center', gap: 0.5,
            }}>
              <CropOutlined sx={{ fontSize: 11, color: PRIMARY }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: PRIMARY }}>
                {forcedLabel}
              </Typography>
            </Box>
          ) : (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={aspectPreset}
              onChange={handleAspectChange}
              sx={{
                '& .MuiToggleButton-root': {
                  color: '#5a6474',
                  borderColor: '#e4e9f0',
                  fontSize: '0.65rem',
                  py: 0.25, px: 0.9,
                  textTransform: 'none',
                  fontWeight: 600,
                  background: '#fff',
                  '&.Mui-selected': {
                    background: alpha(PRIMARY, 0.08),
                    color: PRIMARY,
                    borderColor: alpha(PRIMARY, 0.3),
                  },
                  '&:hover': { background: '#f0f4f8' },
                },
              }}
            >
              {ASPECT_PRESETS.map(p => (
                <ToggleButton key={p.label} value={p.value}>{p.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Box>

        <Tooltip title="Reset crop">
          <IconButton
            size="small"
            onClick={handleReset}
            sx={{ color: '#9ea8b3', borderRadius: '7px', '&:hover': { color: '#374151', background: '#f0f4f8' } }}
          >
            <RestartAltOutlined sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Canvas area ── */}
      <DialogContent
        ref={containerRef}
        sx={{
          flex: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#e2e8f0',
          p: 3,
          overflow: 'hidden',
        }}
      >
        {imgError ? (
          <Typography sx={{ color: '#dc2626', fontSize: '0.8rem' }}>
            Failed to load image for cropping.
          </Typography>
        ) : !imgLoaded ? (
          <CircularProgress size={28} sx={{ color: PRIMARY }} />
        ) : (
          <Box sx={{
            position: 'relative',
            boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
            borderRadius: '3px',
            lineHeight: 0,
            userSelect: 'none',
          }}>
            <canvas
              ref={canvasRef}
              width={canvasSize.w}
              height={canvasSize.h}
              style={{ display: 'block', borderRadius: '3px', touchAction: 'none' }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{
        px: 2.5, py: 1.5,
        borderTop: '1px solid #e8edf2',
        background: '#fff',
        flexShrink: 0,
        justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Box>
          {item?.isCropped && (
            <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3' }}>
              A crop is already applied — saving replaces it. The original is always preserved.
            </Typography>
          )}
          {saveError && (
            <Typography sx={{ fontSize: '0.68rem', color: '#dc2626', fontWeight: 500 }}>
              {saveError}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            size="small"
            sx={{
              borderRadius: '8px', textTransform: 'none', fontSize: '0.78rem',
              borderColor: '#d4dbe6', color: '#5a6474',
              '&:hover': { borderColor: '#9ea8b3', background: '#f8fafc' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !imgLoaded}
            size="small"
            startIcon={saving
              ? <CircularProgress size={13} sx={{ color: '#fff' }} />
              : <CheckOutlined sx={{ fontSize: 15 }} />
            }
            sx={{
              borderRadius: '8px', textTransform: 'none', fontSize: '0.78rem',
              fontWeight: 700, background: PRIMARY, px: 2.5,
              '&:hover': { background: alpha(PRIMARY, 0.85) },
              '&:disabled': { background: '#e4e9f0', color: '#b0bac8' },
            }}
          >
            {saving ? 'Saving…' : 'Save Crop'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}